import React, {useCallback, useEffect, useState, useRef} from 'react'

/**
 * 字典项数据结构（用于前端展示）
 */
export type DictItemDisplay = {
    itemCode: string
    itemName: string
    color: string
    sorter: number
}

/**
 * 字典数据结构
 */
export type DictData = {
    dictCode: string
    items: DictItemDisplay[]
    itemMap: Map<string, DictItemDisplay> // itemCode -> DictItemDisplay
}

/**
 * 字典全局状态
 */
export type DictContextType = {
    dictMap: Map<string, DictData> // dictCode -> DictData
    loaded: boolean
    loading: boolean
    reload: () => void
    // 根据字典代码获取字典项列表（用于下拉选择）
    getSelectOptions: (dictCode: string) => { label: string; value: string }[]
    // 根据字典代码和项目代码获取显示名称
    getItemName: (dictCode: string, itemCode: string) => string | undefined
    // 根据字典代码和项目代码获取颜色（已转换为 antd-mobile 兼容的颜色）
    getItemColor: (dictCode: string, itemCode: string) => string
    // 根据字典代码获取颜色映射（用于 Tag 映射）
    getTagMapping: (dictCode: string) => Record<string, { text: string; color: string }>
}

const DictContext = React.createContext<DictContextType | undefined>(undefined)

/**
 * 字典 Provider 组件
 * 在应用启动时加载所有字典项数据
 */
export function DictProvider({children}: { children: React.ReactNode }) {
    const [dictMap, setDictMap] = useState<Map<string, DictData>>(new Map())
    const [loaded, setLoaded] = useState(false)
    const [loading, setLoading] = useState(false)
    const isLoadingRef = useRef(false)

    const loadDictItems = useCallback(() => {
        // 防止重复请求
        if (isLoadingRef.current) {
            return
        }

        isLoadingRef.current = true
        setLoading(true)

        import('@/api/dict')
            .then((module) => {
                return module.fetchDictItemList()
            })
            .then((data) => {
                const newDictMap = new Map<string, DictData>()

                // 按字典代码分组（数据直接在 data 字段中）
                if (data && Array.isArray(data)) {
                    for (const item of data) {
                        const {dictCode, itemCode, itemName, color, sorter = 0} = item

                        if (!newDictMap.has(dictCode)) {
                            newDictMap.set(dictCode, {
                                dictCode,
                                items: [],
                                itemMap: new Map(),
                            })
                        }

                        const dictData = newDictMap.get(dictCode)!
                        const displayItem: DictItemDisplay = {
                            itemCode: String(itemCode),
                            itemName,
                            // 存储原始的 antd 颜色
                            color,
                            sorter
                        }

                        dictData.items.push(displayItem)
                        dictData.itemMap.set(String(itemCode), displayItem)
                    }

                    // 按排序号排序
                    for (const dictData of newDictMap.values()) {
                        dictData.items.sort((a, b) => a.sorter - b.sorter)
                    }
                }

                setDictMap(newDictMap)
                setLoaded(true)
                isLoadingRef.current = false
                setLoading(false)
            })
            .catch(() => {
                isLoadingRef.current = false
                setLoading(false)
            })
    }, [])

    useEffect(() => {
        // 只在未加载时执行
        if (!loaded && !loading) {
            loadDictItems()
        }
    }, [loadDictItems, loaded, loading])

    const reload = useCallback(() => {
        isLoadingRef.current = false
        loadDictItems()
    }, [loadDictItems])

    const getSelectOptions = useCallback(
        (dictCode: string): { label: string; value: string }[] => {
            const dictData = dictMap.get(dictCode)
            if (!dictData) {
                return []
            }
            return dictData.items.map((item) => ({
                label: item.itemName,
                value: item.itemCode,
            }))
        },
        [dictMap]
    )

    const getItemName = useCallback(
        (dictCode: string, itemCode: string): string | undefined => {
            const dictData = dictMap.get(dictCode)
            if (!dictData) {
                return undefined
            }
            return dictData.itemMap.get(String(itemCode))?.itemName
        },
        [dictMap]
    )

    const getItemColor = useCallback(
        (dictCode: string, itemCode: string): string => {
            const dictData = dictMap.get(dictCode)
            if (!dictData) {
                return '#999'
            }

            const item = dictData.itemMap.get(String(itemCode))
            // 将存储的 antd 颜色转换为 antd-mobile 兼容颜色用于显示
            return item?.color || '#999'
        },
        [dictMap]
    )

    const getTagMapping = useCallback(
        (dictCode: string): Record<string, { text: string; color: string }> => {
            const dictData = dictMap.get(dictCode)
            if (!dictData) {
                return {}
            }
            const mapping: Record<string, { text: string; color: string }> = {}
            for (const item of dictData.items) {
                mapping[item.itemCode] = {
                    text: item.itemName,
                    // 将存储的 antd 颜色转换为 antd-mobile 兼容颜色用于显示
                    color: item.color
                }
            }
            return mapping
        },
        [dictMap]
    )

    const value: DictContextType = {
        dictMap,
        loaded,
        loading,
        reload,
        getSelectOptions,
        getItemName,
        getItemColor,
        getTagMapping,
    }

    return <DictContext.Provider value={value}>{children}</DictContext.Provider>
}

/**
 * 获取字典 Context 的 Hook
 */
export function useDict(): DictContextType {
    const context = React.useContext(DictContext)
    if (!context) {
        throw new Error('useDict must be used within DictProvider')
    }
    return context
}

/**
 * 获取字典选项的 Hook
 * 用于组件获取字典选项数组
 */
export function useDictOptions(dictCode: string): { label: string; value: string }[] {
    const dict = useDict()
    return dict.getSelectOptions(dictCode)
}

/**
 * 字典工具类
 */
export class DictUtil {
    /**
     * 根据字典代码和项目代码获取显示名称
     * 需要在字典加载完成后调用
     */
    static getItemName(dictCode: string, itemCode: string): string {
        const context = this.getContext()
        if (!context) {
            return itemCode
        }
        return context.getItemName(dictCode, itemCode) || itemCode
    }

    /**
     * 根据字典代码和项目代码获取颜色
     */
    static getItemColor(dictCode: string, itemCode: string): string {
        const context = this.getContext()
        if (!context) {
            return '#999'
        }
        return context.getItemColor(dictCode, itemCode)
    }

    private static getContext(): DictContextType | undefined {
        // 从全局变量中获取
        return (globalThis as Record<string, unknown>).__DICT_CONTEXT__ as DictContextType | undefined
    }
}

// 设置全局字典 Context
export function setGlobalDictContext(context: DictContextType) {
    ;(globalThis as Record<string, unknown>).__DICT_CONTEXT__ = context
}
